import { useEffect, useRef, useState } from 'react';

interface IUseGetCardLiveViewSizesProps {
  mainImageWidth: number;
  mainImageHeight: number;
  containerRef: React.RefObject<HTMLDivElement>;
}

const RESIZE_THROTTLE = 16;

export const useGetCardLiveViewSizes = ({
  mainImageWidth,
  mainImageHeight,
  containerRef,
}: IUseGetCardLiveViewSizesProps) => {
  const lastResizeTime = useRef<number>(0);
  const rafRef = useRef<number>();

  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const scaleX = dimensions.width / mainImageWidth;
  const scaleY = dimensions.height / mainImageHeight;

  useEffect(() => {
    const calculateDimensions = () => {
      const node = containerRef?.current;

      if (node) {
        const containerWidth = node.offsetWidth;
        const containerHeight = node.offsetHeight;

        if (mainImageHeight > 0 && mainImageWidth > 0) {
          const newHeight = (mainImageHeight / mainImageWidth) * containerWidth;

          containerRef.current.style.height = 'max-content';
          containerRef.current.style.width = `100%`;
          containerRef.current.style.aspectRatio = `${mainImageWidth}/${mainImageHeight}`;

          setDimensions({ width: containerWidth, height: newHeight });
        }
      }
    };

    const handleResize = () => {
      const now = Date.now();

      if (now - lastResizeTime.current >= RESIZE_THROTTLE) {
        lastResizeTime.current = now;

        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
        }

        rafRef.current = requestAnimationFrame(calculateDimensions);
      }
    };

    calculateDimensions();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [mainImageHeight, mainImageWidth]);

  return { dimensions, scaleX, scaleY };
};
